/**
 * Usage without sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w" />
 *
 * Usage with sizes:
 *   <ResponsiveImage
 *     alt="ListingX"
 *     image={imageDataFromSDK}
 *     variants={['landscape-crop', 'landscape-crop2x']}
 *     sizes="(max-width: 600px) 100vw, 50vw"
 *   />
 *   // produces:
 *   <img
 *     alt="ListingX"
 *     src="url/to/landscape-crop.jpg"
 *     srcSet="url/to/landscape-crop.jpg 400w, url/to/landscape-crop2x.jpg 800w"
 *     sizes="(max-width: 600px) 100vw, 50vw" />
 *
 *   // This means that below 600px image will take as many pixels there are available on current
 *   // viewport width (100vw) - and above that image will only take 50% of the page width.
 *   // Browser decides which image it will fetch based on current screen size.
 *
 * NOTE: for all the possible image variant names and their respective
 * sizes, see the API documentation.
 */

import React, { useEffect, useState } from 'react';
import { arrayOf, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import NoImageIcon from './NoImageIcon';
import css from './ResponsiveImage.module.css';

const ResponsiveImage = props => {
  const {
    className,
    rootClassName,
    alt,
    noImageMessage,
    image,
    customImage,
    variants,
    ...rest
  } = props;

  const [hasError, setHasError] = useState(false);

  const classes = classNames(rootClassName || css.root, className);
  if (customImage) {
    if (hasError) {
      const loadingClasses = classNames(
        rootClassName || css.root,
        css.noImageContainer,
        css.loadingPlaceholder,
        className
      );
      return (
        <div className={loadingClasses}>
          <div className={css.noImageWrapper} />
        </div>
      );
    }

    const { onError, ...restProps } = rest;
    return (
      <img
        alt={alt}
        src={customImage}
        className={classes}
        {...restProps}
        onError={e => {
          setHasError(true);
          if (typeof onError === 'function') onError(e);
        }}
      />
    );
  }
  if (image == null || variants.length === 0) {
    const noImageClasses = classNames(rootClassName || css.root, css.noImageContainer, className);

    const noImageMessageText = noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;
    return (
      <div className={noImageClasses}>
        <div className={css.noImageWrapper}>
          {/* <NoImageIcon className={css.noImageIcon} />
          <div className={css.noImageText}>{noImageMessageText}</div> */}
        </div>
      </div>
    );
  }

  const imageVariants = image?.attributes?.variants || {};

  const srcSet = variants
    .map(variantName => {
      const variant = imageVariants[variantName];

      if (!variant) {
        // Variant not available (most like just not loaded yet)
        return null;
      }
      if (!variant.url || !variant.width) {
        return null;
      }
      return `${variant.url} ${variant.width}w`;
    })
    .filter(v => v != null)
    .join(', ');

  // Always provide a usable src to avoid browsers displaying the alt text when
  // image variants are missing or still loading.
  const requestedVariantsWithUrl = variants
    .map(variantName => imageVariants[variantName])
    .filter(v => v && v.url);

  const fallbackVariant =
    requestedVariantsWithUrl[0] || Object.values(imageVariants).find(v => v && v.url);
  const src = fallbackVariant?.url;

  // Reset error state when the rendered image src changes.
  useEffect(() => {
    setHasError(false);
  }, [src]);

  // If we don't yet have any image URL to render, show a skeleton-like placeholder
  // instead of rendering a broken <img> that may display alt text.
  if (!src) {
    const loadingClasses = classNames(
      rootClassName || css.root,
      css.noImageContainer,
      css.loadingPlaceholder,
      className
    );
    return (
      <div className={loadingClasses}>
        <div className={css.noImageWrapper} />
      </div>
    );
  }

  const imgProps = {
    className: classes,
    src,
    srcSet: srcSet || undefined,
    ...rest,
  };

  if (hasError) {
    const loadingClasses = classNames(
      rootClassName || css.root,
      css.noImageContainer,
      css.loadingPlaceholder,
      className
    );
    return (
      <div className={loadingClasses}>
        <div className={css.noImageWrapper} />
      </div>
    );
  }

  const { onError, ...restImgProps } = imgProps;
  return (
    <img
      alt={alt}
      {...restImgProps}
      onError={e => {
        setHasError(true);
        if (typeof onError === 'function') onError(e);
      }}
    />
  );
};

ResponsiveImage.defaultProps = {
  className: null,
  rootClassName: null,
  image: null,
  noImageMessage: null,
};

ResponsiveImage.propTypes = {
  className: string,
  rootClassName: string,
  alt: string.isRequired,
  image: propTypes.image,
  variants: arrayOf(string).isRequired,
  noImageMessage: string,
};

export default ResponsiveImage;
